import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import RecordVoiceOverRoundedIcon from "@mui/icons-material/RecordVoiceOverRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import HearingRoundedIcon from "@mui/icons-material/HearingRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function DeEsserLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const panelBg = "rgba(255,255,255,0.04)";
  const darkPanel = "rgba(0,0,0,0.18)";

  const articleSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "De-Esser in Music Production: What It Is and How It Works",
      description:
        "Learn what a de-esser does, how it reduces harsh sibilance, where it is used on vocals and dialogue, and how it differs from EQ and compression.",
      author: {
        "@type": "Organization",
        name: "Parish Audio",
      },
      publisher: {
        "@type": "Organization",
        name: "Parish Audio",
        logo: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/assets/logo.png",
        },
      },
      mainEntityOfPage: "https://parishaudio.com/learning/de-esser",
      articleSection: "Audio Education",
      keywords: [
        "de esser",
        "de-esser",
        "what does a de esser do",
        "vocal sibilance",
        "reduce harsh s sounds",
        "de esser vs eq",
        "de esser vs compressor",
      ],
    }),
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "A de-esser reduces harsh sibilant sounds such as S, SH, and CH, usually by turning down a targeted high-frequency range only when it becomes too strong.",
      icon: <HearingRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "Too much sibilance can make a vocal or spoken voice sound sharp, fatiguing, and difficult to listen to, especially in bright modern mixes.",
      icon: <RecordVoiceOverRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Lead vocals, backing vocals, dialogue, podcasts, voiceovers, and sometimes bright instruments that need occasional high-frequency control.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const deEsserTypes = [
    {
      name: "Broadband De-Esser",
      description:
        "Turns down the full signal when sibilance is detected, which can sound smooth but may be more noticeable if overused.",
    },
    {
      name: "Split-Band De-Esser",
      description:
        "Only reduces the offending high-frequency range, often preserving the rest of the signal more transparently.",
    },
    {
      name: "Frequency-Selective De-Esser",
      description:
        "Targets a very specific band where the harshness lives, which can be highly effective when tuned correctly.",
    },
    {
      name: "Dynamic EQ Approach",
      description:
        "Acts similarly to a de-esser by reducing a chosen band only when it becomes excessive, often with more precise control.",
    },
    {
      name: "Multiband De-Esser",
      description:
        "Lets you control several regions independently, which can be useful when harshness exists in more than one area.",
    },
    {
      name: "Manual / Clip-Gain De-Essing",
      description:
        "Instead of using a plugin, the engineer manually turns down specific harsh syllables for maximum precision.",
    },
  ];

  const deEsserConcepts = [
    {
      name: "Sibilance",
      description:
        "Sibilance is the bright hiss-like energy in consonants such as S, SH, and CH. Too much of it can sound piercing and distracting.",
    },
    {
      name: "Detection Range",
      description:
        "A de-esser listens to a targeted part of the frequency spectrum and reacts when that area becomes too loud.",
    },
    {
      name: "Dynamic Reduction",
      description:
        "Unlike static EQ, a de-esser only reduces the problem when it appears, leaving the sound mostly untouched the rest of the time.",
    },
    {
      name: "Control Without Dulling",
      description:
        "The goal is not to remove brightness completely. The goal is to reduce harshness while keeping clarity and intelligibility.",
    },
  ];

  const controls = [
    {
      name: "Frequency",
      description:
        "Sets the area of the spectrum where the de-esser focuses. This is where the harsh consonants are being detected and reduced.",
    },
    {
      name: "Threshold",
      description:
        "Determines how strong the sibilance must be before the de-esser starts working.",
    },
    {
      name: "Amount / Range",
      description:
        "Controls how much reduction the de-esser applies when it engages.",
    },
    {
      name: "Bandwidth / Q",
      description:
        "Shapes how narrow or wide the targeted frequency region is, which affects how focused or broad the processing feels.",
    },
    {
      name: "Monitor / Listen",
      description:
        "Lets you hear what the detector is focusing on, which makes it much easier to find the harsh region accurately.",
    },
    {
      name: "Mode",
      description:
        "Some de-essers let you switch between broadband and split-band behavior depending on the type of result you want.",
    },
  ];

  const commonUses = [
    "Reducing harsh S sounds on lead vocals",
    "Smoothing bright backing vocals so they stack better",
    "Making spoken dialogue easier to listen to for long periods",
    "Taming sharp consonants in podcasts and voiceovers",
    "Controlling occasional harshness on acoustic guitar or cymbals in specific cases",
    "Helping a bright vocal sit in a dense modern mix without sounding piercing",
  ];

  const bestPractices = [
    "Find the exact harsh area before applying reduction. Guessing often leads to dull results.",
    "Use only as much reduction as needed. Too much de-essing can make speech sound lispy or lifeless.",
    "Listen in context with the full mix, because sibilance that sounds obvious in solo may be just right in context.",
    "Try clip gain or automation for problem words if a global de-esser is not solving it cleanly.",
    "Consider whether the real issue is the microphone, vocal technique, or an overly bright EQ boost earlier in the chain.",
  ];

  const relatedLinks = [
    {
      title: "Equalization",
      to: "/learning/equalization",
      description:
        "Learn how EQ shapes tone broadly, while a de-esser only reduces harsh frequencies when they become excessive.",
    },
    {
      title: "Compression",
      to: "/learning/compression",
      description:
        "See how compression can sometimes exaggerate sibilance by bringing quieter consonants forward.",
    },
    {
      title: "Limiting",
      to: "/learning/limiting",
      description:
        "Understand why bright peaks and sibilance can become even more noticeable when a vocal or mix is pushed louder.",
    },
    {
      title: "Saturation",
      to: "/learning/saturation",
      description:
        "Explore how saturation can add presence and harmonics, but sometimes also makes bright consonants more obvious.",
    },
  ];

  const sectionCardSx = {
    p: 2,
    borderRadius: 3,
    width: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
  };

  return (
    <>
      <Seo
        title="De-Esser | Parish Audio Learning"
        description="Learn what a de-esser does, how it reduces harsh sibilance, where it is used on vocals and dialogue, and how it differs from EQ and compression."
        canonical="https://parishaudio.com/learning/de-esser"
        schema={articleSchema}
      />

      <Box ref={topContainer} sx={{ width: "95%", maxWidth: 1100, mx: "auto", minHeight: height - 140 }}>
        <Paper
          sx={{
            mt: 3,
            mb: 4,
            p: { xs: 2, md: 3 },
            background: `linear-gradient(180deg, ${style.color1} 0%, rgba(22,32,48,0.98) 100%)`,
            borderRadius: 3,
            color: style.color4,
            boxShadow: 12,
            overflow: "hidden",
          }}
        >
          <Stack spacing={3.25}>
            <Box>
              <Chip
                label="Learning Center"
                sx={{
                  mb: 1.5,
                  color: style.color4,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", md: "3rem" },
                  fontWeight: 800,
                  lineHeight: 1.05,
                  mb: 1.25,
                  color: style.color4,
                  maxWidth: 900,
                }}
              >
                De-Esser in Music Production: What It Is and How It Works
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.85,
                  maxWidth: 950,
                }}
              >
                A de-esser is a specialized processor designed to reduce harsh sibilance.
                It is most often used on vocals and spoken word, where sharp S, SH, and CH
                sounds can jump out and make the recording feel piercing, brittle, or tiring
                to listen to.
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.85,
                  mt: 2,
                  maxWidth: 950,
                }}
              >
                The key idea behind a de-esser is selective control. Instead of dulling the
                whole track with static EQ, it only reduces the harsh area when it becomes
                excessive. That makes it one of the most useful tools for keeping a vocal
                bright and clear without letting sibilance become distracting.
              </Typography>
            </Box>

            <Grid container spacing={2} alignItems="stretch">
              {quickFacts.map((item) => (
                <Grid item xs={12} md={4} key={item.title} sx={{ display: "flex" }}>
                  <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg, boxShadow: 6 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Box sx={{ flexShrink: 0 }}>{item.icon}</Box>
                      <Box>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.5 }}>
                          {item.title}
                        </Typography>
                        <Typography sx={{ color: style.color3, lineHeight: 1.7, fontSize: "0.97rem" }}>
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                What is a de-esser?
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mb: 2 }}>
                A de-esser is a dynamic processor that detects excessive high-frequency
                sibilance and reduces it only when needed. It is commonly described as a
                frequency-conscious compressor, because it reacts mainly to a problem region
                rather than the entire signal equally.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mb: 2 }}>
                This makes it ideal for vocals and speech. A singer or speaker may sound
                clear and balanced most of the time, but certain consonants can suddenly
                spike in the upper range. Instead of turning the whole track dull, a de-esser
                controls just those moments.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                De-essing is often subtle when done well. The best settings usually make the
                voice feel smoother and more polished without drawing attention to the fact
                that processing is happening.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                How de-essing works
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {deEsserConcepts.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: darkPanel }}>
                      <Typography sx={{ color: accent, fontWeight: 700, mb: 0.75 }}>
                        {item.name}
                      </Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mt: 2 }}>
                In practice, the de-esser listens for a chosen high-frequency area. When a
                harsh consonant becomes too strong, the processor reduces that part of the
                sound briefly. Once the sibilance passes, the signal returns to normal.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Common types of de-essers
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {deEsserTypes.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg, boxShadow: 4 }}>
                      <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}>
                        {item.name}
                      </Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                De-esser vs EQ
              </Typography>

              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: darkPanel,
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                      Static control vs dynamic control
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    EQ turns a frequency range down all the time. A de-esser only reduces it
                    when it becomes a problem. That is why de-essing often sounds more natural
                    on vocals that need to stay bright but not harsh.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    If a vocal is overly bright all the time, EQ may be the right solution.
                    If the harshness only appears on certain words or consonants, a de-esser
                    is often the better choice.
                  </Typography>
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Common de-esser controls
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {controls.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg }}>
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TuneRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                        <Box>
                          <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.5 }}>
                            {item.name}
                          </Typography>
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                How de-essers are used in vocals and dialogue
              </Typography>

              <Paper sx={{ p: 2.25, borderRadius: 3, backgroundColor: panelBg }}>
                <Stack spacing={1.35}>
                  {commonUses.map((item) => (
                    <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                      <LibraryMusicRoundedIcon sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }} />
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mt: 2 }}>
                De-essing is often most effective after compression, because compression can
                make sibilance more obvious by bringing quieter details forward. It is also
                commonly used after bright EQ boosts that add clarity but also expose harsh
                consonants.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                When de-essing goes too far
              </Typography>

              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: darkPanel,
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <Stack spacing={1.35}>
                  {[
                    "The voice can start sounding lispy, smeared, or oddly soft on consonants.",
                    "Important clarity and intelligibility can be reduced along with the harshness.",
                    "The vocal may lose air and life if the chosen frequency area is too broad.",
                    "Aggressive settings can make the processing obvious instead of invisible.",
                  ].map((item) => (
                    <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                      <WarningAmberRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Best practices for using a de-esser
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {bestPractices.map((item, index) => (
                  <Grid item xs={12} md={6} key={index} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: darkPanel }}>
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TipsAndUpdatesRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                        <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                          {item}
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Related learning topics
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {relatedLinks.map((item) => (
                  <Grid item xs={12} md={6} key={item.title} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        ...sectionCardSx,
                        backgroundColor: panelBg,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}>
                          {item.title}
                        </Typography>
                        <Typography sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}>
                          {item.description}
                        </Typography>
                      </Box>

                      <Box>
                        <Button
                          component={RouterLink}
                          to={item.to}
                          variant="outlined"
                          sx={{
                            color: style.color4,
                            borderColor: "rgba(255,255,255,0.18)",
                            "&:hover": {
                              borderColor: accent,
                              backgroundColor: "rgba(255,255,255,0.04)",
                            },
                          }}
                        >
                          Read More
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Final thoughts
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.9, mb: 2 }}>
                A de-esser is a small tool that can make a huge difference in how polished a
                vocal or voice recording feels. By controlling harsh sibilance only when it
                becomes excessive, it helps keep a track smooth, clear, and comfortable to hear.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                The best de-essing usually goes unnoticed. It keeps the brightness and clarity
                you want while gently removing the moments that would otherwise distract the listener.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}